import { useEffect, useState } from 'react';

/**
 * `useIsDesktopViewport()` — the single general-purpose phone/desktop
 * switching mechanism for the app (design/screens/responsive-strategy.md
 * §2.1). Reads `layout.desktopBreakpoint` (1024px) live via
 * `window.matchMedia('(min-width: <breakpoint>px)')`, re-evaluating on
 * resize — the one real case a resized browser window (not the installed
 * PWA) needs live re-evaluation for, per `desktop-shell.md` §1.
 *
 * The breakpoint value itself is read from the CSS custom property already
 * exposed for the token (`--layout-desktop-breakpoint`, tokens.css) rather
 * than a hard-coded literal, so a future token change propagates here
 * without a second edit — falling back to the literal 1024 only if that
 * custom property is ever absent (defensive, not the primary path).
 *
 * SSR/first-paint concern does not apply (client-only Vite SPA, no server
 * render) — the hook may return an initial value synchronously from
 * `matchMedia` at first render, no hydration-mismatch hazard to design
 * around.
 *
 * This is the ONLY general-purpose viewport hook in the app — any future
 * breakpoint check (e.g. `desktop-shell.md`'s own rail-compression logic
 * once that shell is built) should consume this same hook rather than
 * maintaining a second breakpoint check.
 */
export function useIsDesktopViewport(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => queryIsDesktop());

  useEffect(() => {
    const mql = window.matchMedia(mediaQuery());
    const onChange = () => setIsDesktop(mql.matches);
    onChange();
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback (addListener/removeListener are deprecated but
    // still the only API on older engines) — matches the pattern jsdom's
    // own matchMedia stub (src/ui/testing/setup.ts) exposes both forms for.
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isDesktop;
}

function queryIsDesktop(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(mediaQuery()).matches;
}

function mediaQuery(): string {
  return `(min-width: ${breakpointPx()}px)`;
}

function breakpointPx(): number {
  const FALLBACK = 1024;
  if (typeof document === 'undefined') return FALLBACK;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--layout-desktop-breakpoint');
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : FALLBACK;
}
